import { columnsKey, readJSON, writeJSON } from "@khazzin/i18n/storage";
import type { ReactNode } from "react";

/**
 * What a table column is, and which of them a person has chosen to see.
 *
 * The visibility is per screen and per browser, which is the right scope for
 * it: which columns somebody wants on the orders table is a working preference
 * like a window size, not something that should follow them to a colleague's
 * machine. `10/01` §3's saved views are the shareable version, and they are a
 * later story; this is the one-click "I never look at Weight" that every table
 * needs immediately.
 */

/** How a cell is drawn when the table has collapsed to cards. */
export type CardBehaviour =
	/** One label-and-value pair in the two-column card grid. */
	| "default"
	/** Spans both columns. For a name, or anything long. */
	| "wide"
	/** Absent from the card. For a column that only makes sense beside its neighbours. */
	| "hide";

export interface Column<Row> {
	/** Stable across renders and releases: it is what the hidden-column list stores. */
	id: string;
	/** The message key for the header. Never a literal — this is a translated console. */
	headerKey: string;
	cell: (row: Row) => ReactNode;
	/** `end` for numbers. Money and counts are read by their last digit. */
	align?: "start" | "end";
	/** A fixed width in pixels. Most columns should not have one. */
	width?: number;
	/**
	 * The narrowest the column may get, in pixels. For a name column, which
	 * otherwise wraps to one word a line while a long category path beside it
	 * keeps its full width.
	 */
	minWidth?: number;
	/** Monospace: SKUs, ids, barcodes — anything somebody compares character by character. */
	mono?: boolean;
	/** Offer a sort control in the header. The server does the sorting. */
	sortable?: boolean;
	/** Offer it in the column picker. A table's identifying column should not be. */
	hideable?: boolean;
	/** Start hidden. For a column that is useful occasionally and noise the rest of the time. */
	defaultHidden?: boolean;
	card?: CardBehaviour;
}

/** The hidden-column ids for one screen, as they are stored. */
type HiddenColumns = string[];

/**
 * Which columns this browser has hidden on a screen.
 *
 * Stores the *hidden* set rather than the visible one, and that is the load-
 * bearing choice. A stored visible list freezes the table at the moment
 * somebody last touched the picker: add a column in a later release and every
 * person who has ever opened the picker never sees it, because their stored
 * list does not mention it. Storing what was turned off means a new column is
 * visible by default to everybody, which is what a new column is for.
 */
export function readHiddenColumns(screenKey: string): Set<string> {
	return new Set(readJSON<HiddenColumns>(columnsKey(screenKey), []));
}

/** Remembers the hidden set for a screen. */
export function writeHiddenColumns(screenKey: string, hidden: Set<string>): void {
	writeJSON(columnsKey(screenKey), [...hidden]);
}

/**
 * The columns to render, in order.
 *
 * `defaultHidden` applies only to a screen this browser has never had an
 * opinion about. Once somebody opens the picker, their choice is the whole
 * answer — including the choice to turn a `defaultHidden` column on, which
 * would otherwise be reverted on every reload.
 */
export function visibleColumns<Row>(
	columns: Column<Row>[],
	hidden: Set<string>,
	touched: boolean,
): Column<Row>[] {
	return columns.filter((column) => {
		if (column.hideable !== true) {
			return true;
		}
		if (touched) {
			return !hidden.has(column.id);
		}
		return column.defaultHidden !== true;
	});
}

/** The ids a screen starts with hidden, before anybody touches the picker. */
export function defaultHidden<Row>(columns: Column<Row>[]): Set<string> {
	return new Set(
		columns.filter((c) => c.hideable === true && c.defaultHidden === true).map((c) => c.id),
	);
}
